import { Modal } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import IconClock from "../icons/icon-clock";
import { AttendanceService, CHECK_IN_DAY_TYPE, CheckInStatus, CheckLocationScope } from "../../features/human-resources/attendance/services/attendance-service";
import { MediaService } from "../../features/auth/services/auth-service";
import { ButtonCustom } from "../custom/button-custom";
import { useCRUDResponse, useDataResponse } from "../../hooks/useResponseHandler";
import { isSuccess } from "../../models/response/base-response";
import { getDeviceUUID } from "../../utils/helpers";
import { getTodayDate } from "../../utils/time-utils";
import CookieUtils from "../../utils/cookie-utils";
import { ATTENDANCE_STATUS } from "../../constants/status";
import { ApiConfig } from "../../services/api-config";

// Type definitions
interface LocationData {
    latitude: string;
    longitude: string;
    name: string;
    formatted_address: string;
    old_formatted_address: string;
    accuracy?: number;
}

interface LocationErrorProps {
    message: string;
    onRetry?: () => void;
}

const LocationErrorUI: React.FC<LocationErrorProps> = ({ message, onRetry }) => (
    <div className="p-3 mb-4 border border-red-200 rounded-lg bg-red-50">
        <div className="flex items-start space-x-2">
            {/* Inline SVG icon */}
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" className="w-5 h-5 fill-red-600 mt-0.5 flex-shrink-0" viewBox="0 0 16 16">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
            </svg>

            <div className="flex-1">
                <p className="text-sm text-red-800">{message}</p>
                {onRetry && (
                    <button
                        onClick={onRetry}
                        className="mt-2 text-sm font-medium text-red-600 hover:underline"
                    >
                        Thử lại
                    </button>
                )}
            </div>
        </div>
    </div>
);


const CheckInButton: React.FC = () => {

    const [isCheckedIn, setIsCheckedIn] = useState<boolean>(false);
    const [showModalCheckIn, setShowModalCheckIn] = useState<boolean>(false);
    const [locationData, setLocationData] = useState<LocationData>({
        latitude: '', longitude: '', name: '', formatted_address: '', old_formatted_address: ''
    });
    const [loadingLocation, setLoadingLocation] = useState<boolean>(false);
    const [locationError, setLocationError] = useState<string | null>(null);
    // Kết quả kiểm tra vị trí so với bán kính công ty/chi nhánh — chỉ để CẢNH BÁO, không chặn (BE vẫn
    // lưu bình thường khi ngoài bán kính). null = chưa kiểm tra xong hoặc lỗi API (coi như trong phạm vi).
    const [locationScope, setLocationScope] = useState<CheckLocationScope | null>(null);

    // "Hôm nay tôi nên thấy gì" — chỉ NV cần chấm công mới hiện nút; NV giờ hành chính theo lịch cố
    // định, NV theo ca theo ca được xếp; ngày nghỉ/ngày lễ hiện label thay cho nút.
    const [checkInStatus, setCheckInStatus] = useState<CheckInStatus | null>(null);

    // Chụp ảnh khi CHECK-IN — bắt buộc, không áp dụng cho check-out.
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const [isStartingCamera, setIsStartingCamera] = useState<boolean>(false);
    const [cameraError, setCameraError] = useState<string | null>(null);
    const [capturedPhoto, setCapturedPhoto] = useState<{ blob: Blob; previewUrl: string } | null>(null);

    const { fetch: onCheckIn, response: resCheckIn, loading: loadingCheckIn } = AttendanceService.attendanceCheckIn()
    const { fetch: onCheckOut, response: resCheckOut, loading: loadingCheckOut } = AttendanceService.attendanceCheckOut()
    const { fetch: onDailySession, response: resDailySession, loading: loadingDaily } = AttendanceService.attendanceEmployeeDailySession()
    const { fetch: onGetCheckInStatus, response: resCheckInStatus, loading: loadingCheckInStatus } = AttendanceService.checkInStatus()
    const { fetch: onCheckLocationScope, response: resLocationScope } = AttendanceService.checkLocationScope()
    const { uploadFiles: uploadPhoto, isUploading: isUploadingPhoto } = MediaService.Upload()

    useEffect(() => {
        onDailySession({
            date: getTodayDate(),
            employee_id: CookieUtils.getCurrentUser().id ?? 0
        })
        onGetCheckInStatus()
    }, [])

    useDataResponse(resCheckInStatus, (data) => {
        setCheckInStatus(data);
    })

    useDataResponse(resLocationScope, (data) => {
        setLocationScope(data);
    })

    useDataResponse(resDailySession, (data) => {
        if (!data.sessions || data.sessions.length === 0) {
            setIsCheckedIn(false);
            return;
        }
        const lastSession = data.sessions[data.sessions.length - 1];
        // Nếu chưa check out thì coi như đang "đã check in"
        if (
            lastSession.check_out_status === ATTENDANCE_STATUS.NONE ||
            lastSession.check_out_status === ATTENDANCE_STATUS.NOT_CHECKED_OUT
        ) {
            setIsCheckedIn(
                lastSession.check_in_status !== ATTENDANCE_STATUS.NONE &&
                lastSession.check_in_status !== ATTENDANCE_STATUS.NOT_CHECKED_IN
            );
        } else {
            // Đã check out thì coi như chưa check in
            setIsCheckedIn(false);
        }
    })

    useCRUDResponse(resCheckIn, () => {
        setIsCheckedIn(true);
        onGetCheckInStatus();
        setShowModalCheckIn(false);
        setCapturedPhoto((prev) => {
            if (prev) URL.revokeObjectURL(prev.previewUrl);
            return null;
        });
    }, "Check in ca làm việc thành công");

    useCRUDResponse(resCheckOut, () => {
        setIsCheckedIn(false); // Set to false after check out
        setShowModalCheckIn(false);
        // Mỗi ngày chỉ 1 phiên — tải lại để BE trả should_show_button=false + label "Đã chấm công hôm nay".
        onGetCheckInStatus();
    }, "Check out ca làm việc thành công");

    // Get user location
    const getUserLocation = (): Promise<LocationData> => {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error('Geolocation is not supported by this browser.'));
                return;
            }
            setLoadingLocation(true);
            setLocationError(null);
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude, accuracy } = position.coords;
                    try {
                        // Reverse geocoding để lấy địa chỉ
                        const address = await reverseGeocode(latitude, longitude, accuracy);

                        resolve({
                            latitude: latitude.toString(),
                            longitude: longitude.toString(),
                            name: address.name,
                            formatted_address: address.formatted_address,
                            old_formatted_address: address.old_formatted_address,
                            accuracy: accuracy
                        });
                    } catch (error) {
                        // Nếu không lấy được địa chỉ, vẫn trả về tọa độ
                        resolve({
                            latitude: latitude.toString(),
                            longitude: longitude.toString(),
                            name: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
                            formatted_address: '-',
                            old_formatted_address: '-',
                            accuracy: accuracy
                        });
                    }
                    setLoadingLocation(false);
                },
                (error) => {
                    setLoadingLocation(false);
                    let errorMessage = 'Không thể lấy vị trí.';
                    switch (error.code) {
                        case error.PERMISSION_DENIED:
                            errorMessage = 'Bạn đã từ chối cấp quyền truy cập vị trí. Vui lòng bật lại quyền trong trình duyệt hoặc cài đặt hệ thống để sử dụng tính năng chấm công.';
                            break;
                        case error.POSITION_UNAVAILABLE:
                            errorMessage = 'Hệ thống không xác định được vị trí hiện tại. Có thể do mất kết nối mạng, dùng VPN, hoặc dịch vụ định vị của thiết bị tạm thời không khả dụng.';
                            break;
                        case error.TIMEOUT:
                            errorMessage = 'Quá thời gian chờ để lấy vị trí. Vui lòng kiểm tra kết nối mạng và thử lại.';
                            break;
                        default:
                            errorMessage = 'Đã xảy ra lỗi không xác định khi lấy vị trí. Vui lòng thử lại sau.';
                            break;
                    }
                    setLocationError(errorMessage);
                    reject(new Error(errorMessage));
                },
                {
                    enableHighAccuracy: true,
                    timeout: 15000,
                    maximumAge: 60000 // Cache trong 1 phút
                }
            );
        });
    };

    // Reverse geocoding để chuyển tọa độ thành địa chỉ
    const reverseGeocode = async (lat: number, lng: number, radius: number) => {
        try {
            // Sử dụng Nominatim API (free)
            const response = await fetch(
                `https://maps.track-asia.com/api/v2/geocode/json?latlng=${lat},${lng}&key=${ApiConfig.API_MAP_KEY}&new_admin=true&include_old_admin=true&radius=${radius}`
            );
            const data = await response.json();
            if (data && data.results.length > 0) {
                return {
                    name: data.results[0].name,
                    formatted_address: data.results[0].formatted_address,
                    old_formatted_address: data.results[0].old_formatted_address,
                };
            }
            return {
                name: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
                formatted_address: '-',
                old_formatted_address: '-',
            };
        } catch (error) {
            console.error('Reverse geocoding error:', error);
            return {
                name: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
                formatted_address: '-',
                old_formatted_address: '-',
            };
        }
    };

    const getCurrentTime = () => {
        return new Date().toLocaleString('vi-VN', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Prepare data for API call
    const prepareAttendanceData = async (avatar: string) => {
        try {
            const attendanceData = {
                latitude: locationData.latitude,
                longitude: locationData.longitude,
                ip: "",
                device_id: getDeviceUUID(),
                avatar,
                address: `${locationData.name}, ${locationData.old_formatted_address}`
            };
            return attendanceData;
        } catch (error) {
            throw error;
        }
    };

    // ==================== CHỤP ẢNH CHECK-IN (bắt buộc, không áp dụng cho check-out) ====================
    const stopCamera = () => {
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
    };

    const startCamera = async () => {
        setCameraError(null);
        setIsStartingCamera(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
            }
        } catch (error) {
            console.error('Camera error:', error);
            setCameraError('Không thể truy cập camera. Vui lòng cấp quyền camera cho trình duyệt để chấm công.');
        } finally {
            setIsStartingCamera(false);
        }
    };

    const capturePhoto = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas || !video.videoWidth) return;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
            if (!blob) return;
            setCapturedPhoto((prev) => {
                if (prev) URL.revokeObjectURL(prev.previewUrl);
                return { blob, previewUrl: URL.createObjectURL(blob) };
            });
            // Dừng camera ngay sau khi chụp — tránh giữ đèn camera sáng không cần thiết.
            stopCamera();
        }, 'image/jpeg', 0.9);
    };

    const retakePhoto = () => {
        setCapturedPhoto((prev) => {
            if (prev) URL.revokeObjectURL(prev.previewUrl);
            return null;
        });
        void startCamera();
    };

    // ⚠️ TẠM DÙNG ĐỂ TEST LOCAL — bỏ qua gọi "/v1/upload/files" (media service không chạy local),
    // dùng thẳng path mặc định. Đặt lại BYPASS_CHECKIN_UPLOAD = false sau khi test xong.
    const BYPASS_CHECKIN_UPLOAD = false;
    const DEFAULT_CHECKIN_IMAGE_PATH = 'uploads/test-placeholder.jpg';

    // Upload ảnh đã chụp lên media service, trả về path để gửi kèm field `avatar` khi check-in.
    const uploadCapturedPhoto = async (): Promise<string> => {
        if (!capturedPhoto) return '';
        if (BYPASS_CHECKIN_UPLOAD) return DEFAULT_CHECKIN_IMAGE_PATH;
        const file = new File([capturedPhoto.blob], `checkin-${Date.now()}.jpg`, { type: 'image/jpeg' });
        // uploadFiles nhận FileList, không tạo trực tiếp được trong trình duyệt — dùng DataTransfer.
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);

        const res: any = await uploadPhoto(dataTransfer.files);
        if (!res || !isSuccess(res)) return '';

        const item = Array.isArray(res.data) ? res.data[0] : res.data;
        return item?.path ?? item?.url ?? item?.location ?? '';
    };

    const handleCheckIn = async () => {
        try {
            if (locationScope && !locationScope.in_scope) {
                const confirmed = window.confirm(
                    `Bạn đang cách ${locationScope.branch_name} khoảng ${locationScope.distance_m}m (cho phép ${locationScope.radius_m}m). ` +
                    `Bạn có chắc chắn muốn ${isCheckedIn ? 'check out' : 'check in'} tại vị trí hiện tại không?`
                );
                if (!confirmed) return;
            }

            let avatar = '';
            if (!isCheckedIn) {
                // Check-in: bắt buộc phải có ảnh đã chụp — nút xác nhận đã disable nếu chưa chụp,
                // đây là chốt chặn cuối phòng khi state không đồng bộ.
                avatar = await uploadCapturedPhoto();
                if (!avatar) {
                    setCameraError('Tải ảnh chấm công lên thất bại. Vui lòng chụp lại và thử lại.');
                    return;
                }
            }

            const attendanceData = await prepareAttendanceData(avatar);
            if (isCheckedIn) {
                // Check Out
                await onCheckOut(attendanceData);
            } else {
                // Check In
                await onCheckIn(attendanceData);
            }
        } catch (error) {
            console.error('Attendance error:', error);
            // Handle error - có thể show notification
        }
    };

    // Handle modal open - get location (và camera nếu đang check-in) khi modal mở
    const handleOpenModal = async () => {
        setShowModalCheckIn(true);
        setLocationScope(null);
        try {
            const location = await getUserLocation();
            setLocationData(location);
            // Kiểm tra vị trí so với bán kính công ty/chi nhánh — chỉ để hiện cảnh báo, không chặn mở modal.
            void onCheckLocationScope({ latitude: location.latitude, longitude: location.longitude });
        } catch (error) {
            console.error('Location error:', error);
        }
        // Check-out không cần chụp ảnh.
        if (!isCheckedIn) {
            void startCamera();
        }
    };

    const handleCloseModal = () => {
        setShowModalCheckIn(false);
        stopCamera();
        setCapturedPhoto((prev) => {
            if (prev) URL.revokeObjectURL(prev.previewUrl);
            return null;
        });
        setCameraError(null);
        setLocationScope(null);
    };

    // Dừng camera khi component unmount (vd điều hướng trang khác trong lúc modal đang mở).
    useEffect(() => {
        return () => stopCamera();
    }, []);

    // Lưu ý: các hàm render bên dưới ĐỀU là hàm thường (gọi trực tiếp `fn()` trong JSX), KHÔNG khai
    // báo dạng component con (`<Fn />`). Khai dạng component con ở đây (hàm định nghĩa lại mỗi lần
    // render) khiến React coi đó là 1 component type MỚI mỗi render → unmount/remount toàn bộ subtree
    // mỗi khi cha re-render — vỡ hẳn camera (mất `srcObject` đã gán trên node `<video>` cũ mỗi khi
    // `isStartingCamera`/state khác đổi, dù stream vẫn đang chạy ngầm).
    const renderCheckInUIButton = () => (
        <button
            onClick={handleOpenModal}
            className={`flex items-center space-x-2 rounded-lg font-medium transition-all duration-300 px-2 py-1.5 text-sm ${isCheckedIn
                ? 'bg-green-500/10 text-green-600 border border-green-500/20 hover:bg-green-500/20'
                : 'bg-blue-500/10 text-blue-600 border border-blue-500/20 hover:bg-blue-500/20'
                }`}
        >
            <IconClock className="w-4 h-4" />
            <span>{isCheckedIn ? 'Check Out' : 'Check In'}</span>
        </button>
    );

    const renderLocationStatus = () => {
        if (loadingLocation) {
            return (
                <div className="p-3 mb-4 border border-yellow-200 rounded-lg bg-yellow-50">
                    <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-b-2 border-yellow-600 rounded-full animate-spin"></div>
                        <span className="text-sm text-yellow-800">Đang lấy vị trí...</span>
                    </div>
                </div>
            );
        }

        if (locationError) {
            return (
                <LocationErrorUI
                    message={locationError}
                    onRetry={() => {
                        setLocationError(null);
                        getUserLocation()
                            .then(setLocationData)
                            .catch((err) => console.error("Retry failed:", err));
                    }}
                />
            );
        }

        if (locationData) {
            return (
                <div className="p-3 mb-4 border border-green-200 rounded-lg bg-green-50">
                    <div className="text-sm text-green-800">
                        <p className="mb-1 font-medium">📍 Vị trí hiện tại:</p>
                        {locationData.name && (
                            <p className="text-xs font-semibold">{locationData.name}</p>
                        )}
                        {/* Địa chỉ mới */}
                        {locationData.formatted_address && (
                            <p className="mt-1 text-xs">Địa chỉ (mới): {locationData.formatted_address}</p>
                        )}
                        {/* Địa chỉ cũ */}
                        {locationData.old_formatted_address && (
                            <p className="text-xs text-green-600">
                                Địa chỉ (cũ): {locationData.old_formatted_address}
                            </p>
                        )}
                        {locationData.accuracy && (
                            <p className="mt-1 text-xs text-green-600">
                                Độ chính xác: {Math.round(locationData.accuracy)}m
                            </p>
                        )}
                    </div>
                </div>
            );
        }

        return null;
    };

    // Cảnh báo khi vị trí hiện tại ngoài bán kính công ty/chi nhánh — chỉ CẢNH BÁO, không chặn (BE vẫn
    // lưu bình thường), người dùng vẫn phải bấm xác nhận thêm 1 lần khi bấm nút chấm công (handleCheckIn).
    const renderLocationScopeWarning = () => {
        if (!locationScope || locationScope.in_scope) return null;
        return (
            <div className="p-3 mb-4 border border-orange-200 rounded-lg bg-orange-50">
                <p className="text-sm text-orange-800">
                    ⚠️ Vị trí hiện tại đang <strong>ngoài phạm vi</strong> {locationScope.branch_name} — cách khoảng{' '}
                    {locationScope.distance_m}m (cho phép {locationScope.radius_m}m). Bạn vẫn có thể chấm công nhưng sẽ cần xác nhận lại.
                </p>
            </div>
        );
    };

    // Chụp ảnh — chỉ hiện khi đang CHECK-IN (check-out không cần ảnh).
    const renderCameraCapture = () => {
        if (isCheckedIn) return null;

        return (
            <div className="mb-4">
                <p className="mb-2 text-sm font-medium text-gray-700">📷 Ảnh chấm công <span className="text-red-500">*</span></p>

                {cameraError && <LocationErrorUI message={cameraError} onRetry={() => void startCamera()} />}

                {!cameraError && (
                    <div className="overflow-hidden bg-black rounded-lg aspect-video">
                        {capturedPhoto ? (
                            <img src={capturedPhoto.previewUrl} alt="Ảnh chấm công" className="object-cover w-full h-full" />
                        ) : (
                            <video ref={videoRef} muted playsInline className="object-cover w-full h-full scale-x-[-1]" />
                        )}
                    </div>
                )}
                <canvas ref={canvasRef} className="hidden" />

                <div className="flex justify-center mt-2">
                    {capturedPhoto ? (
                        <button
                            type="button"
                            onClick={retakePhoto}
                            className="text-sm font-medium text-blue-600 hover:underline"
                        >
                            Chụp lại
                        </button>
                    ) : (
                        !cameraError && (
                            <ButtonCustom
                                type="button"
                                isLoading={isStartingCamera}
                                disabled={isStartingCamera}
                                onClick={capturePhoto}
                                className="px-4 py-1.5 text-sm text-white bg-gray-700 rounded-lg hover:bg-gray-800 disabled:opacity-50"
                                text={isStartingCamera ? 'Đang mở camera...' : 'Chụp ảnh'}
                            />
                        )
                    )}
                </div>
            </div>
        );
    };

    // "Hôm nay tôi nên thấy gì" trên header — NOT_APPLICABLE (NO_ATTENDANCE): ẩn hẳn, không hiện gì.
    // Ngày lễ/ngày nghỉ: ẩn nút, hiện label thay thế. Lỗi tải status: fail-open — vẫn hiện nút như cũ
    // để không lỡ chặn nhầm NV thực sự cần chấm công vì 1 lỗi API tạm thời.
    const renderCheckInWidget = () => {
        if (loadingDaily || loadingCheckInStatus) return null;
        if (checkInStatus?.day_type === CHECK_IN_DAY_TYPE.NOT_APPLICABLE) return null;

        if (checkInStatus && !checkInStatus.should_show_button) {
            return (
                <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-100 px-2 py-1.5 text-sm font-medium text-gray-500">
                    <IconClock className="w-4 h-4" />
                    <span>{checkInStatus.label}</span>
                </div>
            );
        }

        return renderCheckInUIButton();
    };

    return (
        <>
            {renderCheckInWidget()}
            <Modal
                centered
                opened={showModalCheckIn}
                size={'auto'}
                onClose={handleCloseModal}
                withCloseButton={false}
                // transitionProps={{ transition: "pop", duration: 350, exitDuration: 350 }}
            >
                {/* max-h + overflow-y-auto: nội dung (ảnh chấm công + vị trí + giờ) có thể vượt quá
                chiều cao màn hình, nhất là trên màn nhỏ — không có dòng này modal bị tràn không cuộn
                được. */}
                <div className="bg-white rounded-lg shadow-xl w-[35rem] max-h-[85vh] flex flex-col">
                    <div className="p-6 overflow-y-auto">
                        <div className="flex items-center justify-center mb-4">
                            <div className={`flex justify-center items-center w-16 h-16 rounded-full ${isCheckedIn ? 'bg-red-100' : 'bg-blue-100'}`}>
                                <IconClock className={`w-8 h-8 ${isCheckedIn ? 'text-red-600' : 'text-blue-600'}`} />
                            </div>
                        </div>

                        <h2 className="mb-2 text-xl font-bold text-center text-gray-800">
                            Xác nhận {isCheckedIn ? 'Check Out' : 'Check In'}
                        </h2>

                        <p className="mb-6 text-center text-gray-600">
                            Bạn có chắc chắn muốn {isCheckedIn ? 'check out' : 'check in'} không?
                        </p>

                        {/* Ảnh chấm công (chỉ check-in) */}
                        {renderCameraCapture()}

                        {/* Location Status */}
                        {renderLocationStatus()}
                        {renderLocationScopeWarning()}

                        <div className="p-4 mb-6 rounded-lg bg-gray-50">
                            <div className="text-center">
                                <p className="mb-1 text-sm text-gray-500">Thời gian</p>
                                <p className="font-semibold text-gray-800">{getCurrentTime()}</p>
                            </div>
                        </div>

                        <div className="flex space-x-3">
                            <ButtonCustom
                                type="button"
                                onClick={handleCloseModal}
                                className="flex justify-center flex-1 px-4 py-2 text-gray-700 transition-colors bg-gray-100 rounded-lg hover:bg-gray-200"
                                text="Đóng"
                            />

                            <ButtonCustom
                                type="button"
                                isLoading={loadingCheckIn || loadingCheckOut || loadingLocation || isUploadingPhoto}
                                onClick={handleCheckIn}
                                disabled={loadingLocation || Boolean(locationError) || (!isCheckedIn && !capturedPhoto)}
                                className={`${isCheckedIn ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'} flex flex-1 justify-center px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                                text={
                                    loadingLocation ? 'Đang lấy vị trí...' :
                                        isUploadingPhoto ? 'Đang tải ảnh...' :
                                            `Xác nhận ${isCheckedIn ? 'Check Out' : 'Check In'}`
                                }
                            />
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    )
}

export default CheckInButton